import { useRoleClient } from '@/components/Apollo/client';
import { useAuthContext } from '@/components/Auth/AuthContext';
import BaseContainer from '@/components/BaseContainer';
import BaseDropDownPicker from '@/components/Form/BaseDropDownPicker';
import HabitudesStatsChart from '@/components/ScreenComponents/HabitudesStatsChart';
import LogMarStatsChart from '@/components/ScreenComponents/LogMarStatsChart';
import StatsBarChart from '@/components/ScreenComponents/StatsBarChart';
import StatsLineChart from '@/components/ScreenComponents/StatsLineChart';
import ScreenMargin from '@/components/ScreenMargin';
import SimpleLink from '@/components/SimpleLink';
import useFormStyle from '@/components/Styles/useFormStyle';
import {
  useListChirurgiensQuery,
  useStatsParChirurgienLazyQuery,
  useStatsUtilisationLazyQuery,
} from '@/data/data';
import { Color } from '@/GlobalStyles';
import DateUtils from '@/utils/DateUtils';
import StringUtils from '@/utils/StringUtils';
import { Redirect } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

const sexes = [
  { value: 0, label: 'Tous' },
  { value: -1, label: 'Non défini' },
  { value: 1, label: 'Homme' },
  { value: 2, label: 'Femme' },
];

const StatsPage = () => {
  const client = useRoleClient('admin');
  const [getStatsUtilisation, { data: statsUtilisation, loading }] = useStatsUtilisationLazyQuery({
    client,
  });
  const { data: listChirurgiens } = useListChirurgiensQuery({ client });
  const [getStatsParChirurgien, { data: statsParChirurgien, loading: loadingStatsParChirurgien }] =
    useStatsParChirurgienLazyQuery({ client });
  const { t, i18n } = useTranslation();
  const authUser = useAuthContext();
  const [sexeFilter, setSexeFilter] = useState<number | null>(null);
  const [chirurgienFilter, setChirurgienFilter] = useState<string[] | null>(null);

  const styles = useFormStyle();

  useEffect(() => {
    getStatsParChirurgien({
      variables: {
        sexe_param: sexeFilter !== 0 && sexeFilter !== null ? sexeFilter : null,
        where:
          chirurgienFilter !== null && chirurgienFilter.length > 0
            ? {
                chirurgienId: {
                  _in: chirurgienFilter,
                },
              }
            : undefined,
        chirurgien_id_param:
          chirurgienFilter !== null && chirurgienFilter.length > 0
            ? StringUtils.toPGSQLArrayLitteral(chirurgienFilter)
            : null,
      },
      client,
    });
    getStatsUtilisation({
      variables: {
        chirurgien_id_param:
          chirurgienFilter !== null && chirurgienFilter.length > 0
            ? StringUtils.toPGSQLArrayLitteral(chirurgienFilter)
            : null,
      },
      client,
    });
  }, [sexeFilter, chirurgienFilter]);

  if (authUser.payload && authUser.payload['cognito:groups'].indexOf('admin') <= -1) {
    return <Redirect href={'/patients'} />;
  }

  let graphData: [string, number, number, number, number][] = [];
  let graphDataAge: [string, number, number, number, number][] = [];
  if (statsUtilisation) {
    const chirData = Object.fromEntries(
      statsUtilisation.evolutionChirurgiens.map((v) => [v.mois, v.nombreChirurgiens])
    );

    //on utilise les mois de patientMoyenParChirurgiens car par construction tous les mois seront présents
    graphData = statsUtilisation.getPatientMoyenParChirurgiens.map((v) => [
      DateUtils.convertMonth(v.mois),
      chirData[v.mois] || 0,
      v.nombrePatients,
      chirurgienFilter !== null && chirurgienFilter.length > 0
        ? null
        : (v.recrutementMoyenParChirurgien || 0).toFixed(2),
      0,
    ]);
    graphData.map((v, i) => {
      graphData[i][4] = (i > 0 ? graphData[i - 1][4] : 0) + graphData[i][1];
    }, 0);
  }
  if (statsParChirurgien) {
    graphDataAge = statsParChirurgien.getDistributionAgePatients.map((v) => [
      v.trancheAge || '',
      v.nombrePatients || 0,
      v.hommes || 0,
      v.femmes || 0,
      v.nonPrecises || 0,
    ]);
  }

  const chirData = [{ value: '', label: 'Tous' }];
  listChirurgiens?.chirurgien.forEach((v) => {
    chirData.push({
      value: v.id,
      label: v.nom + ' ' + v.prenom + (v.clinique ? ' - ' + v.clinique : ''),
    });
  });

  const implantsStats: {
    [k: string]: {
      type: string;
      nb: number;
      pourcentage: number;
      data: { materiau?: string | null; nb: number; pourcentage: number }[];
    };
  } = {};
  if (statsParChirurgien?.getRepartitionImplants) {
    statsParChirurgien?.getRepartitionImplants.map((v) => {
      if (v.type) {
        if (implantsStats[v.type]) {
          implantsStats[v.type].nb += v.nombreImplants;
          implantsStats[v.type].pourcentage += v.pourcentage;
          implantsStats[v.type].data.push({
            materiau: v.materiau,
            nb: v.nombreImplants,
            pourcentage: v.pourcentage,
          });
        } else {
          implantsStats[v.type] = {
            type: v.type,
            nb: v.nombreImplants,
            pourcentage: v.pourcentage,
            data: [{ materiau: v.materiau, nb: v.nombreImplants, pourcentage: v.pourcentage }],
          };
        }
      }
    });
  }

  return (
    <ScreenMargin>
      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 20, zIndex: 1 }}>
        <View style={[{ flex: 1 }]}>
          <BaseDropDownPicker
            setValue={setChirurgienFilter}
            value={chirurgienFilter}
            searchable
            multiple={true}
            placeholder={'Choisissez un chirurgien'}
            items={chirData}
            style={[Platform.OS === 'web' ? { zIndex: 'auto' } : {}]}
            containerStyle={[{ flex: 1 }]}
          />
          <View style={{ flexDirection: 'row', gap: 20, justifyContent: 'flex-end' }}>
            {/* - 1 car il y a le "Tous" */}
            {chirurgienFilter?.length !== chirData?.length - 1 ? (
              <SimpleLink
                onPress={() =>
                  setChirurgienFilter(
                    chirData?.map((d) => d.value || null).filter((d) => d !== null)
                  )
                }
              >
                Tout cocher
              </SimpleLink>
            ) : null}
            {chirurgienFilter?.length > 0 ? (
              <SimpleLink onPress={() => setChirurgienFilter(null)}>Tout décocher</SimpleLink>
            ) : null}
          </View>
        </View>
        <BaseDropDownPicker
          setValue={setSexeFilter}
          value={sexeFilter}
          placeholder={'Choisissez un sexe'}
          items={sexes}
          style={[Platform.OS === 'web' ? { zIndex: 'auto' } : {}]}
          containerStyle={[{ flex: 1 }]}
        />
      </View>
      <BaseContainer>
        <Text style={styles?.headerForm}>{t("Statistiques d'utilisation")}</Text>
        <View style={style.row}>
          <View style={[style.figureContainer, { flexGrow: 0, flexShrink: 0 }]}>
            <Text style={style.figureLabel}>Nombre de chirurgiens</Text>
            <Text style={style.figure}>
              {statsUtilisation?.chirurgienAggregate.aggregate?.count}
            </Text>
          </View>
          <View style={style.graphContainer}>
            <StatsLineChart data={graphData} />
          </View>
        </View>
        <Text style={styles?.headerForm}>{t('Statistiques patients')}</Text>

        <View style={[style.row, { alignItems: 'flex-end' }]}>
          <View style={style.figureContainer}>
            <Text style={style.figureLabel}>Nombre de patients</Text>
            <Text style={style.figure}>
              {statsParChirurgien?.patientAggregate?.aggregate?.count}
            </Text>
          </View>
          <View style={{ flex: 3 }}>
            <Text style={style.figureLabel}>Répartition par sexe</Text>
            <View style={[style.row, { marginBottom: 0 }]}>
              {statsParChirurgien?.getRepartitionSexesPatients.map((v) => {
                let sexe = 'Non définit';
                switch (v.sexe) {
                  case 1:
                    sexe = 'Homme';
                    break;
                  case 2:
                    sexe = 'Femme';
                    break;
                  default:
                }
                return (
                  <View style={style.figureContainer}>
                    <Text style={style.figureSubLabel}>{sexe}</Text>
                    <Text style={style.figure}>{v.nombre}</Text>
                    <Text style={style.subFigure}>{v.pourcentage.toFixed(2)} %</Text>
                  </View>
                );
              })}
            </View>
          </View>

          <View style={style.figureContainer}>
            <Text style={style.figureLabel}>Age moyen</Text>
            <Text style={style.figure}>
              {statsParChirurgien?.getMoyenneAgeTaillePatients?.[0].ageMoyen?.toFixed(2)} ans
            </Text>
          </View>

          <View style={style.figureContainer}>
            <Text style={style.figureLabel}>Taille moyenne</Text>
            <Text style={style.figure}>
              {statsParChirurgien?.getMoyenneAgeTaillePatients?.[0].tailleMoyenne?.toFixed(2)} cm
            </Text>
          </View>
        </View>
        <View style={style.row}>
          <View style={[style.graphContainer, { flexBasis: '70%' }]}>
            <Text style={style.figureLabel}>Répartition par tranche d'âge</Text>
            <StatsBarChart data={graphDataAge} type={'bar'} />
          </View>
          <View style={style.graphContainer}>
            <Text style={style.figureLabel}>Répartition des habitudes de vision</Text>
            <HabitudesStatsChart
              language={i18n.language}
              vl={statsParChirurgien?.getRepartitionVisionPatients?.[0].pourcentageVl}
              vi={statsParChirurgien?.getRepartitionVisionPatients?.[0].pourcentageVi}
              vp={statsParChirurgien?.getRepartitionVisionPatients?.[0].pourcentageVp}
            />
          </View>
        </View>
        <View style={style.row}>
          <View style={style.graphContainer}>
            <Text style={style.figureLabel}>Répartition des activités préférentielles</Text>
            <LogMarStatsChart
              data={statsParChirurgien?.getRepartitionActivitePreferentiellePatients?.map((v) => [
                v.logmarValue,
                v.nombrePersonnes,
              ])}
            />
          </View>
          <View style={[style.graphContainer, { flexGrow: 0, flexShrink: 0 }]}>
            <Text style={style.figureLabel}>Utilisation des implants</Text>
            {Object.values(implantsStats).map((v) => {
              return (
                <View
                  style={[
                    style.row,
                    { flexDirection: 'column', alignItems: 'center', minWidth: 300 },
                  ]}
                >
                  <View
                    style={[
                      {
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: 300,
                        width: '100%',
                      },
                    ]}
                  >
                    <Text style={[style.figureLabel, { marginTop: 0 }]}>{v.type}</Text>
                    <Text style={style.figure}>{v.nb}</Text>
                    <Text style={style.subFigure}>{v.pourcentage.toFixed(2)} %</Text>
                    <View style={[style.row, { width: '100%' }]}>
                      {v.data.map((v2) => {
                        return (
                          <View style={style.figureContainer}>
                            <Text style={style.figureSubLabel}>{v2.materiau}</Text>
                            <Text style={style.figure}>{v2.nb}</Text>
                            <Text style={style.subFigure}>{v2.pourcentage.toFixed(2)} %</Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      </BaseContainer>
    </ScreenMargin>
  );
};

export default StatsPage;

const style = StyleSheet.create({
  figure: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  subFigure: {
    fontSize: 18,
    marginBottom: 10,
  },
  figureLabel: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Color.MainBleu,
    marginTop: 20,
    marginBottom: 5,
    textAlign: 'center',
  },
  figureContainer: {
    flex: 1,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: Color.MainBleu,
    borderRadius: 8,
    padding: 10,
    flexBasis: 'auto',
    flexShrink: 0,
    minHeight: 115,
  },
  graphContainer: {
    flex: 1,
    borderWidth: 2,
    borderColor: Color.MainBleu,
    borderRadius: 8,
    padding: 10,
    flexBasis: 'auto',
    flexShrink: 0,
  },
  figureSubLabel: {
    fontSize: 14,
    marginBottom: 5,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginBottom: 20,
    alignItems: 'flex-start',
    gap: 20,
  },
});
